import {
  Card,
  CardContent,
  CardProps,
  Stack,
  Typography,
  TypographyProps,
} from '@mui/material';
import { defaultValueFormatter } from './data-card.utils';
import Slot from './slot/slot.component';
import { useTranslation } from 'react-i18next';

export type DataCardProps<ExtraSlots = {}, ExtraSlotProps = {}> = {
  title?: string;
  /**
   * For 'currency' type, the value can be a number or a string. If it is a number, it should be an integer multiplied by 1000. If it is a string, it can be a float with a decimal point and should not be multiplied.
   */
  value?: string | number;
  valueType?: 'number' | 'currency' | 'percent' | 'string';
  valueFormatter?: (value: string | number) => string;
  description?: string;
  loading?: boolean;
  isError?: boolean;
  'data-testid'?: string;
  children?: React.ReactNode;
  slots?: {
    title?: React.ReactNode;
    value?: React.ReactNode;
    description?: React.ReactNode;
    afterContent?: React.ReactNode;
  } & ExtraSlots;
  slotProps?: {
    card?: CardProps;
    title?: TypographyProps & { 'data-testid'?: string };
    value?: TypographyProps & { 'data-testid'?: string };
    description?: TypographyProps & { 'data-testid'?: string };
  } & ExtraSlotProps;
};

export const DataCard = ({
  title,
  value,
  valueType = 'number',
  valueFormatter,
  description,
  slots = {},
  slotProps = {},
  loading,
  isError,
  'data-testid': dataTestId = 'DataCard',
  children,
}: DataCardProps) => {
  const { t } = useTranslation();

  return (
    <Card
      {...slotProps.card}
      data-testid={dataTestId}
      sx={{ display: 'flex', position: 'relative', flexDirection: 'column' }}
    >
      <CardContent
        sx={{
          '&:last-child': { paddingBottom: 2 },
          display: 'flex',
          flexGrow: 1,
        }}
      >
        {!isError ? (
          <Stack
            spacing={0.5}
            sx={{
              flexGrow: 1,
            }}
          >
            <Slot
              slot={slots.title}
              shouldRenderDefault={!!title}
              loading={loading && !slots.title && !title}
              SkeletonProps={{ width: '60%', height: 18 }}
            >
              <Typography
                variant="body2"
                component="div"
                margin={0}
                {...slotProps.title}
              >
                {title}
              </Typography>
            </Slot>
            <Slot
              slot={slots.value}
              shouldRenderDefault={value != null}
              loading={loading}
              SkeletonProps={{ width: '35%', height: 24 }}
            >
              <Typography
                variant="cardValue"
                component="div"
                {...slotProps.value}
              >
                {defaultValueFormatter(value, valueType, { valueFormatter })}
              </Typography>
            </Slot>
            <Slot
              slot={slots.description}
              shouldRenderDefault={!!description}
              loading={loading}
              SkeletonProps={{ width: '100%', height: 18 }}
            >
              <Typography
                variant="body2"
                component="div"
                margin={0}
                {...slotProps.description}
              >
                {description}
              </Typography>
            </Slot>
            {children}
          </Stack>
        ) : (
          <Typography component="text" margin="auto">
            {t('dataCard.error')}
          </Typography>
        )}
      </CardContent>
      {slots.afterContent && slots.afterContent}
    </Card>
  );
};

export default DataCard;
